import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import {
  FormattedDate,
  FormattedMessage,
  FormattedRelativeTime,
} from 'react-intl';
import PropTypes from 'prop-types';
import { NoteShape } from './shape';

const Note = ({
  currentEmployee,
  disableMode,
  editMode,
  isLoading,
  note,
  onCancel,
  onEdit,
  onSave,
  onRemove,
  charactersRemaining
}) => {
  const {
    content,
    isEditing,
    timestamp,
    employee: {
      id: cardEmployeeId,
      firstName,
      lastName,
      lasName, //mm-web model must be bad :=(
    } = {},
  } = note;
  const [editorValue, setEditorValue] = useState(content);
  const [charactersLeft, setCharactersLeft]= useState(charactersRemaining);
  const hasCharRestriction = charactersRemaining != null;
  const cssBEM = 'note-card';
  const classes = classnames(cssBEM, {
    [`${cssBEM}--editMode`]: editMode,
    [`${cssBEM}--disabled`]: disableMode,
    [`${cssBEM}--not-saved`]: isEditing && !editMode,
  });
  const cardBelongsToCurrentEmployee = !currentEmployee || !currentEmployee.id || (currentEmployee.id && currentEmployee.id === cardEmployeeId);
  useEffect(() => {
    if (hasCharRestriction) {
      setCharactersLeft(charactersRemaining - editorValue.length);
    }
  }, [editorValue]);

  const renderTimestamp = () => {
    const now = new Date();
    const noteTimestamp = new Date(timestamp);
    let timeToFormat = noteTimestamp;
    let Component = FormattedDate;
    if (now.getDate() === noteTimestamp.getDate()) {
      timeToFormat = -((now.getTime() - noteTimestamp.getTime()) / 1000);
      Component = FormattedRelativeTime;
    }

    return (
      <Component value={timeToFormat} updateIntervalInSeconds={10}>
        {(time) => (
          <FormattedMessage
            id="notes.note.details.timestamp"
            values={{ time }}
            tagName="span"
          />
        )}
      </Component>
    );
  };

  const contentArea = editMode ? (
    <div className={`${cssBEM}__content-area ${cssBEM}__editor`}>
      <label>
        <FormattedMessage id="notes.note.editor.label" />
      </label>
      <textarea
        value={editorValue}
        onChange={(e) => setEditorValue(e.target.value)}
        maxLength={hasCharRestriction ? charactersRemaining : null}
      />
    </div>
  ) : (
    <div className={`${cssBEM}__content-area`}>
      <div className={`${cssBEM}__content`}>
        {isEditing ? (
          <span className={`${cssBEM}__not-saved`}>
            <FormattedMessage id="notes.note.not-saved" />
          </span>
        ) : (
          content.split('\n').map((s, k) => (
            <span key={k}>
              {s}
              <br />
            </span>
          ))
        )}
      </div>
      <span className={`${cssBEM}__details`}>
        {firstName || lastName ? (
          <FormattedMessage
            id="notes.note.details.name"
            values={{ firstName, lastName: lastName || lasName }}
            tagName="span"
          />
        ) : null}
        {timestamp ? renderTimestamp() : null}
      </span>
    </div>
  );

  return (
    <section className={classes}>
      {contentArea}
      {editMode && hasCharRestriction ? (
        <div className={`${cssBEM}__characters`}>
          <span>
            <FormattedMessage id="notes.note.charactersLeft" tagName="span"/>
            {charactersLeft}
          </span>
        </div>
      ): null}
      <div className={`${cssBEM}__actions`}>
        {!editMode && cardBelongsToCurrentEmployee ? (
          <button
            className="note-card-btn note-card-btn--edit"
            onClick={() => onEdit(note)}
          >
            <FormattedMessage id="notes.note.edit" />
          </button>
        ) : null}
        {!editMode && cardBelongsToCurrentEmployee ? (
          <button
            className="note-card-btn note-card-btn--remove"
            onClick={() => onRemove(note)}
            disabled={isLoading}
          >
            <FormattedMessage id="notes.note.remove" />
          </button>
        ) : null}
        {editMode ? (
          <button
            className="note-card-btn note-card-btn--cancel"
            onClick={() => onCancel(note)}
          >
            <FormattedMessage id="notes.note.cancel" />
          </button>
        ) : null}
        {editMode ? (
          <button
            className="note-card-btn note-card-btn--save"
            onClick={() => onSave(note, editorValue)}
            disabled={isLoading}
          >
            <FormattedMessage id="notes.note.save" />
          </button>
        ) : null}
      </div>
    </section>
  );
};

Note.propTypes = {
  currentEmployee: PropTypes.any,
  charactersRemaining: PropTypes.any,
  disableMode: PropTypes.bool,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
  note: NoteShape,
  onCancel: PropTypes.func,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
  onSave: PropTypes.func,
};

export default Note;
